import type { StaffMember, StaffRole } from '@/types';
import type { Role } from '@/hooks/useRoles';

/** DB names of the four seeded system roles — domain data, not UI copy. */
export const SYSTEM_ROLE_NAME_TO_LEGACY: Record<string, string> = {
  Admin: 'admin',
  Työnjohto: 'manager',
  Mekaanikko: 'mechanic',
  Vastaanotto: 'reception',
};

export const LEGACY_TO_SYSTEM_ROLE_NAME: Record<string, string> = {
  admin: 'Admin',
  manager: 'Työnjohto',
  mechanic: 'Mekaanikko',
  reception: 'Vastaanotto',
};

export type TeamMember = StaffMember & {
  roleId?: string | null;
  roleName?: string | null;
};

export function settingsRoleKey(staffRole: string): string {
  return `roles.${staffRole}`;
}

export function roleDefinitionLabel(role: Role, t: (key: string) => string): string {
  if (role.isSystem) {
    const legacy = SYSTEM_ROLE_NAME_TO_LEGACY[role.name];
    if (legacy === 'admin') return t('roles.admin');
    if (legacy === 'manager') return t('roles.manager');
    if (legacy === 'mechanic') return t('roles.mechanic');
    if (legacy === 'reception') return t('roles.receptionist');
  }
  return role.name;
}

/** Custom tenant role names are domain data; system roles stay on settings i18n keys. */
export function memberRoleLabel(
  roleName: string | null | undefined,
  staffRole: StaffRole | string,
  t: (key: string) => string,
): string {
  if (roleName && !SYSTEM_ROLE_NAME_TO_LEGACY[roleName]) {
    return roleName;
  }
  return t(settingsRoleKey(staffRole));
}

export function defaultMechanicRoleId(roles: Role[]): string {
  const active = roles.filter((role) => role.isActive);
  const mechanic = active.find((role) => role.isSystem && role.name === 'Mekaanikko');
  return mechanic?.id ?? active[0]?.id ?? '';
}

export function resolveMemberRoleId(member: TeamMember, roles: Role[]): string {
  const active = roles.filter((role) => role.isActive);
  if (member.roleId && active.some((role) => role.id === member.roleId)) {
    return member.roleId;
  }
  const backend =
    member.role === 'owner' ? 'admin' : member.role === 'receptionist' ? 'reception' : member.role;
  const systemName = LEGACY_TO_SYSTEM_ROLE_NAME[backend];
  const match = active.find((role) => role.isSystem && role.name === systemName);
  return match?.id ?? member.roleId ?? '';
}
